import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Stack,
  Grid,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import CampaignIcon from '@mui/icons-material/Campaign';
import BalanceIcon from '@mui/icons-material/Balance';
import HeroImage from '../assets/images/join-the-team.png';
import CustodyImage from '../assets/images/5050-custody.png';
import TimeTakenTimeBackImage from '../assets/images/time-taken-time-back.png';
import ThreeStrikesImage from '../assets/images/three-strikes.png';
import CamerasImage from '../assets/images/cameras-in-courtrooms.png';
import AppealableOrdersImage from '../assets/images/appealable-temporary-orders.png';
import ChildSupportImage from '../assets/images/child-support-dual-calculation-reform.png';
import LitigantAccessImage from '../assets/images/litigant-access-economy-act.png';
import JusticeDelayedImage from '../assets/images/justice-delayed-justice-denied.png';
import SEO from '../components/SEO';
import { trackJoinTeamSubmit } from '../components/GoogleAnalytics';
import { useAnalytics } from '../hooks/useAnalytics';
import { generateWebPageSchema } from '../utils/schemaGenerators';

const GHL_FORM_ID = 'FW3X2NqrPmX3ewvbVypa';
const GHL_IFRAME_ID = `inline-${GHL_FORM_ID}`;
const GHL_FORM_URL = `https://api.leadconnectorhq.com/widget/form/${GHL_FORM_ID}`;

const JoinTheTeam: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(true);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const formSectionRef = useRef<HTMLDivElement>(null);

  const handleScrollToForm = () => {
    analytics.trackEvent('cta_click', 'engagement', 'Become an Advocate - Join Team');
    formSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
  };

  // GHL initialization handshake
  useEffect(() => {
    const handleGHLInit = (event: MessageEvent) => {
      if (!Array.isArray(event.data) || event.data[0] !== 'fetch-query-params') return;
      const iframe = iframeRef.current;
      if (!iframe?.contentWindow) return;
      iframe.contentWindow.postMessage(
        ['query-params', {}, window.location.href, document.referrer, GHL_IFRAME_ID],
        '*',
      );
    };
    window.addEventListener('message', handleGHLInit);
    return () => window.removeEventListener('message', handleGHLInit);
  }, []);

  // Detect GHL form submission
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const knownGHLDomains = ['leadconnectorhq.com', 'msgsndr.com', 'gohighlevel.com'];
      const isFromGHL = knownGHLDomains.some((domain) => event.origin.includes(domain));
      if (!isFromGHL) return;

      const payload =
        typeof event.data === 'string' ? event.data.toLowerCase() : JSON.stringify(event.data ?? '').toLowerCase();

      const isSubmittedEvent =
        payload.includes('form submitted') ||
        payload.includes('form_submitted') ||
        payload.includes('formsubmitted') ||
        payload.includes('submission_success') ||
        payload.includes('submitted') ||
        payload.includes('thank you') ||
        payload.includes('thankyou');

      if (isSubmittedEvent) {
        setIsFormSubmitted(true);
        setIsFormLoading(false);
        trackJoinTeamSubmit(true);
        setSuccessDialogOpen(true);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleMessengerClick = () => {
    analytics.trackExternalLink(
      'https://m.me/cm/AbZlwjVEA-yf4JnK/?send_source=cm:copy_invite_link',
      'Facebook Messenger Community',
    );
    analytics.trackButtonClick('Join Messenger Community', 'Success Dialog');
  };

  return (
    <Container component="main" maxWidth={false}>
      <SEO
        title="RobertGarza.us Family Court Reform - Join the Movement"
        description="Join our global network fighting for comprehensive family court reform. Help advance legislation for shared parenting and accountability worldwide."
        path="/join-the-team"
        schema={generateWebPageSchema({
          name: 'RobertGarza.us Family Court Reform - Join the Movement',
          description:
            'Join our global network fighting for comprehensive family court reform. Help advance legislation for shared parenting and accountability worldwide.',
          url: '/join-the-team',
        })}
      />

      {/* Hero Banner */}
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          textAlign: 'left',
          marginX: { xs: -2, md: -4 },
          padding: { xs: 4, md: 20 },
          backgroundImage: (theme) => ({
            xs: `linear-gradient(to top, ${theme.palette.background.default} 0%, transparent 170%), url(${HeroImage})`,
            md: `linear-gradient(to top, ${theme.palette.background.default} 0%, transparent 70%), url(${HeroImage})`,
          }),
          backgroundPosition: {
            xs: 'left top, 41% top',
            md: 'top right, top right',
          },
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <Box
          sx={{
            maxWidth: { xs: '100%', md: '800px' },
          }}
        >
          <Typography
            variant="h1"
            component="h1"
            gutterBottom
            sx={{ fontWeight: '800', mb: 1, textShadow: '0 2px 8px rgba(0,0,0,0.6)' }}
          >
            Become an Advocate
          </Typography>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontWeight: '400',
              lineHeight: '1.3',
              mb: 2,
              color: 'primary.main',
              textShadow: '0 2px 8px rgba(0,0,0,0.6)',
            }}
          >
            Be the Voice for Family Court Reform
          </Typography>
          <Typography
            variant="h5"
            component="h5"
            sx={{ fontWeight: '200', lineHeight: '1.5', mb: 4, textShadow: '0 1px 6px rgba(0,0,0,0.6)' }}
          >
            Are you passionate about protecting children, supporting shared parenting, and holding family courts
            accountable? We're building a global network of advocates just like you.
          </Typography>
          <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
            <Button
              variant="contained"
              size="large"
              onClick={handleScrollToForm}
              startIcon={<PersonAddIcon />}
              sx={{ backgroundColor: 'primary.main', px: 4, py: 2 }}
            >
              Join the Movement Now
            </Button>
            <Button variant="outlined" size="large" href="/bills" sx={{ px: 4, py: 2 }}>
              Explore Bills
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ py: { xs: 8, md: 20 }, px: { xs: 1, md: 4 } }}>
        <Container>
          {/* What You'll Do Section */}
          <Typography variant="h2" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 6 }}>
            What You'll Do as an Advocate
          </Typography>

          <Typography variant="body1" sx={{ fontSize: '1.2rem', lineHeight: '1.8', mb: 6, textAlign: 'center' }}>
            We're building a global network of advocates, people willing to take action in their local communities by
            urging state legislators to sponsor real, family-first reform.
          </Typography>

          <Grid container spacing={4} sx={{ mb: 8 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
                <CardContent>
                  <GroupWorkIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Connect with Legislators
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                    Work with your state legislators to introduce or co-sponsor key bills from the Family Court Reform
                    Bill Stack
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
                <CardContent>
                  <CampaignIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Raise Awareness
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                    Help educate your community about the harms of biased custody decisions, lack of transparency, and
                    judicial overreach
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
                <CardContent>
                  <BalanceIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Be Their Voice
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                    Represent parents and children who need a stronger voice in your state's legal system
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="body1" sx={{ fontSize: '1.2rem', lineHeight: '1.8', mb: 6, textAlign: 'center' }}>
            Whether you've lived the system yourself or simply care about fairness and reform, your local presence makes
            the difference.
          </Typography>
        </Container>
      </Box>

      {/* The Mission Section */}
      <Box
        sx={{
          py: { xs: 8, md: 20 },
          px: { xs: 4 },
          marginX: -4,
          bgcolor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100]),
        }}
      >
        <Container>
          <Typography variant="h2" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 2 }}>
            The Mission
          </Typography>

          <Typography variant="body1" sx={{ fontSize: '1.2rem', lineHeight: '1.8', mb: 6, textAlign: 'center' }}>
            We're advancing legislation like:
          </Typography>

          {/* Reform Bills Grid */}
          <Grid container spacing={3} sx={{ mb: 6 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: (theme) => theme.shadows[8],
                  },
                }}
                onClick={() => {
                  analytics.trackEvent('bill_card_click', 'bills', 'a-new-start-at-equality');
                  navigate('/bill/a-new-start-at-equality');
                }}
              >
                <Box
                  component="img"
                  src={CustodyImage}
                  alt="50/50 Custody Presumption"
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                  }}
                />
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    A New Start at Equality
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: (theme) => theme.shadows[8],
                  },
                }}
                onClick={() => {
                  analytics.trackEvent('bill_card_click', 'bills', 'time-taken-time-back');
                  navigate('/bill/time-taken-time-back');
                }}
              >
                <Box
                  component="img"
                  src={TimeTakenTimeBackImage}
                  alt="Time Taken, Time Back"
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                  }}
                />
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Time Taken, Time Back
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: (theme) => theme.shadows[8],
                  },
                }}
                onClick={() => {
                  analytics.trackEvent('bill_card_click', 'bills', 'three-strikes');
                  navigate('/bill/three-strikes');
                }}
              >
                <Box
                  component="img"
                  src={ThreeStrikesImage}
                  alt="The Three Strikes Law"
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                  }}
                />
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Three Strikes
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: (theme) => theme.shadows[8],
                  },
                }}
                onClick={() => {
                  analytics.trackEvent('bill_card_click', 'bills', 'cameras-in-courtrooms');
                  navigate('/bill/cameras-in-courtrooms');
                }}
              >
                <Box
                  component="img"
                  src={CamerasImage}
                  alt="Cameras in the Courtroom"
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                  }}
                />
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Cameras in Courtrooms
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: (theme) => theme.shadows[8],
                  },
                }}
                onClick={() => {
                  analytics.trackEvent('bill_card_click', 'bills', 'appealable-temporary-orders');
                  navigate('/bill/appealable-temporary-orders');
                }}
              >
                <Box
                  component="img"
                  src={AppealableOrdersImage}
                  alt="Appealable Temporary Orders"
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                  }}
                />
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Appealable Temporary Orders
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: (theme) => theme.shadows[8],
                  },
                }}
                onClick={() => {
                  analytics.trackEvent('bill_card_click', 'bills', 'child-support-dual-calculation-reform');
                  navigate('/bill/child-support-dual-calculation-reform');
                }}
              >
                <Box
                  component="img"
                  src={ChildSupportImage}
                  alt="Child Support Dual Calculation Reform"
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                  }}
                />
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Child Support Dual Calculation Reform
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: (theme) => theme.shadows[8],
                  },
                }}
                onClick={() => {
                  analytics.trackEvent('bill_card_click', 'bills', 'litigant-access-economy-act');
                  navigate('/bill/litigant-access-economy-act');
                }}
              >
                <Box
                  component="img"
                  src={LitigantAccessImage}
                  alt="The Litigant Access and Economy Act"
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                  }}
                />
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    The Litigant Access and Economy Act
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: (theme) => theme.shadows[8],
                  },
                }}
                onClick={() => {
                  analytics.trackEvent('bill_card_click', 'bills', 'justice-delayed-is-justice-denied');
                  navigate('/bill/justice-delayed-is-justice-denied');
                }}
              >
                <Box
                  component="img"
                  src={JusticeDelayedImage}
                  alt="Justice Delayed, Justice Denied"
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                  }}
                />
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Justice Delayed is Justice Denied
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="body1" sx={{ fontSize: '1.2rem', lineHeight: '1.8', textAlign: 'center' }}>
            These are ready-to-sponsor bills, and you'll be equipped with talking points, bill text, and support along
            the way.
          </Typography>
        </Container>
      </Box>

      {/* Ready to Get Involved Section */}
      <Box sx={{ py: { xs: 8, md: 20 }, px: { xs: 1, md: 4 } }}>
        <Container>
          <Typography variant="h2" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 2 }}>
            Ready to Get Involved?
          </Typography>

          <Typography variant="body1" sx={{ fontSize: '1.2rem', lineHeight: '1.8', mb: 6, textAlign: 'center' }}>
            Fill out the form below to let us know you're ready to be a local advocate. Once submitted, you'll receive
            onboarding materials and next steps to begin making an impact in your district.
          </Typography>

          {/* Inline GHL Form */}
          <Box ref={formSectionRef} sx={{ mb: 8 }}>
            <Typography variant="h3" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 4 }}>
              Join the Movement Now
            </Typography>

            {isFormSubmitted ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Thanks for joining the movement.
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto', mt: 1 }}>
                  Your form was submitted successfully. We are excited to have you on board as we build momentum for
                  real family court reform.
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: 600,
                  mx: 'auto',
                  minHeight: 735,
                }}
              >
                {isFormLoading && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      position: 'absolute',
                      inset: 0,
                      bgcolor: 'background.paper',
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )}
                <iframe
                  ref={iframeRef}
                  src={GHL_FORM_URL}
                  style={{
                    width: '100%',
                    height: '735px',
                    border: 'none',
                    borderRadius: '3px',
                    display: 'block',
                  }}
                  allow="clipboard-write; fullscreen"
                  id={GHL_IFRAME_ID}
                  data-layout="{'id':'INLINE'}"
                  data-trigger-type="alwaysShow"
                  data-trigger-value=""
                  data-activation-type="alwaysActivated"
                  data-activation-value=""
                  data-deactivation-type="neverDeactivate"
                  data-deactivation-value=""
                  data-form-name="JoinTheMovement"
                  data-height="735"
                  data-layout-iframe-id={GHL_IFRAME_ID}
                  data-form-id={GHL_FORM_ID}
                  title="Join the Movement Form"
                  onLoad={() => setIsFormLoading(false)}
                />
              </Box>
            )}
          </Box>

          <Typography variant="h3" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 4 }}>
            Together, we can fix family courts, one state at a time.
          </Typography>
        </Container>
      </Box>

      {/* Success Dialog */}
      <Dialog
        open={successDialogOpen}
        onClose={handleSuccessDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            textAlign: 'center',
          },
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CheckCircleIcon
              sx={{
                fontSize: 48,
                color: 'success.main',
                mb: 1,
              }}
            />
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
              Welcome to the Team!
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleSuccessDialogClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ textAlign: 'center', px: 4, pb: 2 }}>
          <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, mb: 2 }}>
            We've received your submission — welcome aboard! You're now part of a growing movement working to reform
            family courts and restore parental rights worldwide.
          </Typography>

          <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, mb: 2, fontWeight: 'bold' }}>
            👥 Join Our Facebook Messenger Community
          </Typography>

          <Typography variant="body2" sx={{ fontSize: '1rem', lineHeight: 1.6, mb: 3, color: 'text.secondary' }}>
            We just launched a new Messenger group where advocates like you can connect, collaborate, and help move
            legislation forward. Since you're one of the earliest members, your participation is vital in helping us
            grow and organize.
          </Typography>

          <Button
            variant="contained"
            size="large"
            href="https://m.me/cm/AbZlwjVEA-yf4JnK/?send_source=cm:copy_invite_link"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleMessengerClick}
            sx={{
              mb: 3,
              backgroundColor: '#0084ff',
              '&:hover': { backgroundColor: '#0066cc' },
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 'bold',
            }}
          >
            👉🏿 Join the Messenger Advocate Group
          </Button>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}></DialogActions>
      </Dialog>
    </Container>
  );
};

export default JoinTheTeam;
